import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Pencil, Plus, RotateCcw, Tag, Trash2 } from 'lucide-react';

import { cn, toaster, useDebouncedValue, useMediaQuery } from '@/shared/libs';
import {
  Button,
  Dialog,
  DialogFooter,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  ListEmpty,
  ListToolbar,
  type ListToolbarViewMode,
  LoaderOverlay,
  PageHeader,
  Pagination,
  type PaginationPageSize,
  TableController,
  type TableHeader,
} from '@/shared/ui';
import {
  CompactOfferRow,
  fetchOfferDuplicateDraft,
  OFFER_STATUS_I18N_KEY,
  OfferCard,
  useDeleteOfferMutation,
  useOffers,
  useRestoreOfferMutation,
} from '@entities/Offer';
import { useProviderOptions, useProviders } from '@entities/Provider';
import type { Offer, OfferDuplicateDraft } from '@psp-aggregator/sdk';

import { PauseOfferModal } from '../PauseOfferModal';

import { OfferDetailsModal } from '../OfferDetailsModal';
import { OfferFormModal } from '../OfferFormModal';
import { OfferSelectionBar } from '../OfferSelectionBar';
import { OffersFilters, type OffersFilterValues } from '../OffersFilters';

export interface OffersListProps {
  defaultProviderId?: string;
  onGoToDeals?: () => void;
  onDefaultProviderConsumed?: () => void;
  onSelectedOffersChange?: (offers: Offer[]) => void;
  onSendProposal?: (offers: readonly Offer[]) => void;
  selectedOffers?: readonly Offer[];
  title?: string;
}

const EMPTY_FILTERS: OffersFilterValues = {
  countries: [],
  currencies: [],
  integrationMethods: [],
  providerIds: [],
  sort: 'newest',
  statuses: [],
  trafficTypes: [],
};

const isPausedOffer = (offer: Offer): boolean =>
  (offer.status ?? 'active') === 'paused';

export const OffersList: FC<OffersListProps> = ({
  defaultProviderId,
  onDefaultProviderConsumed,
  onSelectedOffersChange,
  onSendProposal,
  selectedOffers,
  title,
}) => {
  const { t } = useTranslation();
  const isNarrow = useMediaQuery('(max-width: 767px)');
  const [view, setView] = useState<ListToolbarViewMode>('cards');
  const effectiveView: ListToolbarViewMode = isNarrow ? 'cards' : view;
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());
  const [filters, setFilters] = useState<OffersFilterValues>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PaginationPageSize>(20);
  const [createOpen, setCreateOpen] = useState(Boolean(defaultProviderId));
  const [editOffer, setEditOffer] = useState<Offer | null>(null);
  const [duplicate, setDuplicate] = useState<OfferDuplicateDraft | null>(null);
  const [detailOffer, setDetailOffer] = useState<Offer | null>(null);
  const [statusOffer, setStatusOffer] = useState<Offer | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Offer | null>(null);
  const deleteOffer = useDeleteOfferMutation();
  const restoreOffer = useRestoreOfferMutation();
  const [internalCart, setInternalCart] = useState<Offer[]>([]);
  const cart = selectedOffers ?? internalCart;

  const offersQuery = useOffers({
    countries: filters.countries.length > 0 ? filters.countries : undefined,
    currencies: filters.currencies.length > 0 ? filters.currencies : undefined,
    integrationMethods:
      filters.integrationMethods.length > 0
        ? filters.integrationMethods
        : undefined,
    isDeleted: showDeleted,
    limit: pageSize,
    page,
    providerIds:
      filters.providerIds.length > 0 ? filters.providerIds : undefined,
    search: debouncedSearch || undefined,
    sort: filters.sort,
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    trafficTypes:
      filters.trafficTypes.length > 0 ? filters.trafficTypes : undefined,
  });
  const providersQuery = useProviders({ limit: 100, page: 1 });
  const optionsQuery = useProviderOptions();
  const pageItems = offersQuery.data?.items ?? [];
  const total = offersQuery.data?.total ?? 0;

  const providerNameById: Record<string, string> = {};

  for (const provider of providersQuery.data?.items ?? []) {
    providerNameById[provider.id] = provider.alias || provider.real_name;
  }

  const hasActiveFilters =
    search.trim() !== '' ||
    filters.statuses.length > 0 ||
    filters.countries.length > 0 ||
    filters.currencies.length > 0 ||
    filters.integrationMethods.length > 0 ||
    filters.providerIds.length > 0 ||
    filters.trafficTypes.length > 0;
  const isListEmpty = !offersQuery.isLoading && total === 0;

  const updateFilters = (next: OffersFilterValues): void => {
    setFilters(next);
    setPage(1);
  };

  const clearFilters = (): void => {
    setSearch('');
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const updateCart = (next: Offer[]): void => {
    if (onSelectedOffersChange) {
      onSelectedOffersChange(next);

      return;
    }

    setInternalCart(next);
  };

  const toggleSelected = (offer: Offer): void => {
    if (isPausedOffer(offer)) {
      return;
    }

    const exists = cart.some((item) => item.id === offer.id);

    updateCart(
      exists ? cart.filter((item) => item.id !== offer.id) : [...cart, offer],
    );
  };

  const handleRestore = (offer: Offer): void => {
    void restoreOffer.mutateAsync(offer.id).catch((error: unknown) => {
      toaster.error({
        title: isAxiosError(error)
          ? (error.response?.data?.message ?? t('errors.offers.restoreFailed'))
          : t('errors.offers.restoreFailed'),
      });
    });
  };

  const handleCopy = async (offer: Offer): Promise<void> => {
    try {
      setDuplicate(await fetchOfferDuplicateDraft(offer.id));
    } catch (error) {
      toaster.error({
        title: isAxiosError(error)
          ? (error.response?.data?.message ?? t('errors.unknown'))
          : t('errors.unknown'),
      });
    }
  };

  const headers: TableHeader[] = [
    { title: '', value: 'select' },
    { key: true, title: t('pages.admin.offers.columns.alias'), value: 'alias' },
    { title: t('pages.admin.offers.columns.provider'), value: 'provider' },
    { title: t('pages.admin.offers.columns.rates'), value: 'rates' },
    { title: t('pages.admin.offers.columns.status'), value: 'status' },
    { title: '', value: 'actions' },
  ];

  const rows = pageItems.map((offer) => {
    const status = offer.status ?? 'active';

    return {
      actions: (
        <div className="inline-flex items-center gap-1">
          {!showDeleted && (
            <button
              aria-label={t('buttons.edit')}
              className="cursor-pointer rounded-lg p-1.5 text-muted-foreground hover:bg-brand/10 hover:text-brand"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setEditOffer(offer);
              }}>
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {!showDeleted && (
            <button
              aria-label={t('buttons.delete')}
              className="cursor-pointer rounded-lg p-1.5 text-rose-500 hover:bg-rose-500/10"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setDeleteTarget(offer);
              }}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          {showDeleted && (
            <button
              aria-label={t('buttons.restore')}
              className="cursor-pointer rounded-lg p-1.5 text-emerald-500 hover:bg-emerald-500/10"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                handleRestore(offer);
              }}>
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
      alias: <CompactOfferRow offer={offer} />,
      id: offer.id,
      provider: providerNameById[offer.provider_id ?? ''] ?? '—',
      rates: <CompactOfferRow pricingOnly offer={offer} />,
      select: (
        <input
          checked={cart.some((item) => item.id === offer.id)}
          className={
            showDeleted || isPausedOffer(offer)
              ? 'cursor-not-allowed'
              : 'cursor-pointer'
          }
          disabled={showDeleted || isPausedOffer(offer)}
          type="checkbox"
          onChange={() => toggleSelected(offer)}
          onClick={(event) => event.stopPropagation()}
        />
      ),
      status: (
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[10px]',
            status === 'active'
              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
              : 'bg-muted text-muted-foreground',
          )}>
          {t(OFFER_STATUS_I18N_KEY[status])}
        </span>
      ),
    };
  });

  if (offersQuery.isLoading && !offersQuery.data) {
    return <LoaderOverlay show />;
  }

  if (offersQuery.isError) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('errors.offers.loadFailed')}</EmptyTitle>
          <EmptyDescription>{t('errors.unknown')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div>
      {!!title && (
        <PageHeader
          action={
            <Button
              size="sm"
              type="button"
              variant="brand"
              onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('pages.admin.offers.create')}
            </Button>
          }
          className="mb-6"
          title={title}
        />
      )}
      <ListToolbar
        count={pageItems.length}
        total={total}
        view={isNarrow ? undefined : { onChange: setView, value: view }}
      />
      <OffersFilters
        countries={optionsQuery.data?.countries.map((item) => item.code) ?? []}
        currencies={
          optionsQuery.data?.currencies.map((item) => item.code) ?? []
        }
        integrations={
          optionsQuery.data?.integration_methods.map((item) => item.code) ?? []
        }
        providers={(providersQuery.data?.items ?? []).map((provider) => ({
          label: provider.alias || provider.real_name,
          value: provider.id,
        }))}
        search={search}
        values={filters}
        onChange={updateFilters}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
      />
      <div className="mb-4">
        <Button
          size="sm"
          type="button"
          variant={showDeleted ? 'brand' : 'outline'}
          onClick={() => {
            setShowDeleted((current) => !current);
            setPage(1);
          }}>
          {showDeleted
            ? t('pages.admin.offers.showActive')
            : t('pages.admin.offers.showTrash')}
        </Button>
      </div>
      {isListEmpty ? (
        <ListEmpty
          icon={Tag}
          variant={hasActiveFilters ? 'filtered' : 'empty'}
          onAction={hasActiveFilters ? clearFilters : () => setCreateOpen(true)}
        />
      ) : effectiveView === 'cards' ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {pageItems.map((offer) => {
            const paused = isPausedOffer(offer);

            return (
              <OfferCard
                key={offer.id}
                offer={offer}
                paused={paused}
                providerName={providerNameById[offer.provider_id ?? ''] ?? '—'}
                selected={cart.some((item) => item.id === offer.id)}
                onCopy={
                  showDeleted
                    ? undefined
                    : (item) => {
                        void handleCopy(item);
                      }
                }
                onDelete={showDeleted ? undefined : setDeleteTarget}
                onEdit={showDeleted ? undefined : setEditOffer}
                onOpen={setDetailOffer}
                onRestore={showDeleted ? handleRestore : undefined}
                onSelect={showDeleted || paused ? undefined : toggleSelected}
                onToggleStatus={showDeleted ? undefined : setStatusOffer}
              />
            );
          })}
        </div>
      ) : (
        <TableController
          data={rows}
          emptyState={{
            filtered: hasActiveFilters,
            onClearFilters: clearFilters,
          }}
          headers={headers}
          onClickRow={(_event, row) => {
            const match = pageItems.find((item) => item.id === row.id);

            if (match) {
              setDetailOffer(match);
            }
          }}
        />
      )}
      {!isListEmpty && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      )}
      {createOpen && (
        <OfferFormModal
          defaultProviderId={defaultProviderId}
          onClose={() => {
            setCreateOpen(false);
            onDefaultProviderConsumed?.();
          }}
        />
      )}
      {!!editOffer && (
        <OfferFormModal offer={editOffer} onClose={() => setEditOffer(null)} />
      )}
      {!!duplicate && (
        <OfferFormModal
          duplicate={duplicate}
          onClose={() => setDuplicate(null)}
        />
      )}
      {!!detailOffer && (
        <OfferDetailsModal
          offer={detailOffer}
          providerName={providerNameById[detailOffer.provider_id ?? '']}
          selected={cart.some((item) => item.id === detailOffer.id)}
          onClose={() => setDetailOffer(null)}
          onCopy={
            showDeleted
              ? undefined
              : () => {
                  void handleCopy(detailOffer);
                  setDetailOffer(null);
                }
          }
          onEdit={
            showDeleted
              ? undefined
              : () => {
                  setEditOffer(detailOffer);
                  setDetailOffer(null);
                }
          }
          onSelect={
            showDeleted || isPausedOffer(detailOffer)
              ? undefined
              : () => toggleSelected(detailOffer)
          }
          onToggleStatus={
            showDeleted
              ? undefined
              : () => {
                  setStatusOffer(detailOffer);
                  setDetailOffer(null);
                }
          }
        />
      )}
      {!!statusOffer && (
        <PauseOfferModal
          offer={statusOffer}
          onClose={() => setStatusOffer(null)}
        />
      )}
      {!!onSendProposal && (
        <OfferSelectionBar
          count={cart.length}
          onClear={() => updateCart([])}
          onSendProposal={() => onSendProposal(cart)}
        />
      )}
      {!!deleteTarget && (
        <Dialog
          title={t('pages.admin.offers.deleteTitle')}
          width={480}
          onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-muted-foreground">
            {t('pages.admin.offers.deleteDescription')}
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteTarget(null)}>
              {t('buttons.cancel')}
            </Button>
            <Button
              disabled={deleteOffer.isPending}
              type="button"
              variant="destructive"
              onClick={() => {
                void deleteOffer
                  .mutateAsync(deleteTarget.id)
                  .then(() => setDeleteTarget(null))
                  .catch((error: unknown) => {
                    toaster.error({
                      title: isAxiosError(error)
                        ? (error.response?.data?.message ??
                          t('errors.offers.deleteFailed'))
                        : t('errors.offers.deleteFailed'),
                    });
                  });
              }}>
              {t('buttons.delete')}
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </div>
  );
};

OffersList.displayName = 'OffersList';
